import { Box, Paper, Typography } from '@mui/material';
import type { MouseEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CustomerAddress, CustomerDto, UserRef } from '@app/shared';
import { tokens } from '../../app/theme';

/** Small building blocks and wording shared by the customer pages. */

export const customerPath = (id: string) => `/customers/${id}`;

/**
 * Customer pages that have their own entry in the Customer Service menu. The entry opens the
 * customer search (`/customers/find/<key>`); a result opens `path` under the customer.
 */
export const CUSTOMER_SECTIONS = [
  { key: 'billing', label: 'Billing and Invoice', code: 'PC75', path: 'billing' },
  { key: 'orders', label: 'Order History', code: 'PC76', path: 'orders' },
  { key: 'purchases', label: 'Purchase History', code: 'PC58', path: 'purchases' },
  { key: 'tickets', label: 'Tickets', code: 'PC87', path: 'tickets' },
  { key: 'activity', label: 'Customer Activity Log', code: 'PC770', path: 'activity' },
  { key: 'cards', label: 'Manage Saved Card', code: 'PC900', path: 'cards' },
  { key: 'notifications', label: 'Resend Notification', code: 'PC930', path: 'notifications' },
] as const;

/** Page title of a customer, as the reference shows it: "<NAME> (Customer ID-n)". */
export const customerTitle = (c: Pick<CustomerDto, 'firstName' | 'lastName' | 'customerId'>) =>
  `${c.firstName} ${c.lastName} (Customer ID-${c.customerId})`;

/**
 * The sentence under the profile title. Only the two cases seen in the reference have one;
 * for every other state nothing is shown, because the reference's wording is not known.
 */
export function statusSentence(c: CustomerDto): string | null {
  if (!c.line.sim) return 'The device assignment process is not yet completed.';
  if (c.status === 'Dispatched') return 'Device has been shipped to the customer.';
  return null;
}

export const streetOf = (a: CustomerAddress): string =>
  [a.line1, a.line2].filter(Boolean).join(', ');
export const addressLine = (a: CustomerAddress): string =>
  [streetOf(a), a.city, [a.state, a.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');

export const userLabel = (user: UserRef | null): string =>
  user ? `${user.name} (${user.loginId})` : '';
export const yesNo = (value: boolean): string => (value ? 'Yes' : 'No');

/** Card with a title bar, used for each section of the profile and of the Modify page. */
export function Section({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper variant="outlined" component="section" aria-label={title} sx={{ mb: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          bgcolor: tokens.cardHeader,
          borderBottom: `1px solid ${tokens.border}`,
        }}
      >
        <Typography variant="h3" component="h2" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        {actions}
      </Box>
      <Box sx={{ p: 1.5 }}>{children}</Box>
    </Paper>
  );
}

/** Grid for the label and value pairs of a section. */
export function FieldGrid({ children }: { children: ReactNode }) {
  return (
    <Box
      component="dl"
      sx={{
        m: 0,
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      }}
    >
      {children}
    </Box>
  );
}

/** One read-only label and value. An empty value shows a dash. */
export function Field({ label, value }: { label: string; value: ReactNode }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <Box>
      <Typography component="dt" variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography component="dd" sx={{ m: 0, overflowWrap: 'anywhere' }}>
        {empty ? '-' : value}
      </Typography>
    </Box>
  );
}

/**
 * Makes every row of the customer table inside it open that customer's profile, as the
 * reference's result list does. The shared `DataTable` has no row click, so the click is
 * caught here: the row is recognised by the element carrying `data-customer-id`, which the
 * customer ID cell renders as a real link for keyboard users.
 */
export function CustomerRowLinks({
  children,
  open: destination = customerPath,
}: {
  children: ReactNode;
  /** Where a row leads; the profile unless the search was opened for another page. */
  open?: (id: string) => string;
}) {
  const navigate = useNavigate();
  const open = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    // The link in the row navigates by itself.
    if (target.closest('a, button')) return;
    const id = target.closest('tr')?.querySelector<HTMLElement>('[data-customer-id]')
      ?.dataset.customerId;
    if (id) navigate(destination(id));
  };
  return (
    <Box onClick={open} sx={{ '& tbody tr.MuiTableRow-hover': { cursor: 'pointer' } }}>
      {children}
    </Box>
  );
}
